/**
 * Component Constants
 * Standard values for components - Single source of truth for component styling
 * 
 * This file contains reusable constants for component styling to ensure consistency
 * across the entire application.
 */

/**
 * Input Heights
 * Standard heights for input components
 */
export const INPUT_HEIGHTS = {
	base: 'h-10',      // 40px - Base input (non-form inputs)
	form: 'h-11',      // 44px - Form inputs (standard for all form fields)
} as const;

/**
 * Button Heights
 * Standard heights for button components
 */
export const BUTTON_HEIGHTS = {
	sm: 'h-9',         // 36px - Small buttons
	md: 'h-10',        // 40px - Medium buttons (default)
	lg: 'h-12',        // 48px - Large buttons
} as const;

/**
 * Border Opacity Classes
 * Standard border opacity utilities using design tokens
 */
export const BORDER_OPACITY = {
	subtle: 'border-subtle',      // 5% opacity - Very subtle borders
	standard: 'border-standard',  // 10% opacity - Standard borders (most common)
	visible: 'border-visible',    // 20% opacity - More visible borders
	focus: 'border-focus',         // 40% opacity - Focus states
} as const;

/**
 * Border Radius Classes
 * Standard border radius values
 */
export const BORDER_RADIUS = {
	none: 'rounded-none',
	sm: 'rounded-sm',     // 4px
	md: 'rounded-md',     // 6px
	lg: 'rounded-lg',     // 8px
	xl: 'rounded-xl',     // 12px
	'2xl': 'rounded-2xl', // 16px
	'3xl': 'rounded-3xl', // 20px
	full: 'rounded-full', // 9999px
	// Custom radius values (from design tokens)
	'14': 'rounded-14',   // 14px
	'18': 'rounded-18',   // 18px
	'25': 'rounded-25',   // 25px
	'26': 'rounded-26',   // 26px
} as const;

/**
 * Font Sizes
 * Standard font sizes for input and select components
 */
export const FONT_SIZES = {
	input: 'text-xs',        // 12px - Standard font size for input text and select text
	placeholder: 'text-xs',  // 12px - Standard font size for placeholder text
	label: 'text-sm',        // 14px - Standard font size for labels
	authButton: 'text-xs',   // 12px - Standard font size for authentication provider buttons
} as const;

/**
 * Shadow Classes
 * Standard shadow utilities
 */
export const SHADOWS = {
	none: 'shadow-none',
	xs: 'shadow-xs',
	sm: 'shadow-sm',
	md: 'shadow-md',
	lg: 'shadow-lg',
	xl: 'shadow-xl',
	custom: 'shadow-custom', // Most common custom shadow
} as const;

/**
 * Spacing Classes
 * Standard spacing utilities (using design tokens)
 */
export const SPACING = {
	xs: 'p-xs m-xs gap-xs',
	sm: 'p-sm m-sm gap-sm',
	md: 'p-md m-md gap-md',
	lg: 'p-lg m-lg gap-lg',
	xl: 'p-xl m-xl gap-xl',
} as const;

/**
 * Common Component Patterns
 * Reusable class combinations for common patterns
 */
export const COMPONENT_PATTERNS = {
	// Standard form input styling
	formInput: `${INPUT_HEIGHTS.form} ${BORDER_OPACITY.standard} rounded-md`,
	
	// Standard card styling
	card: `${BORDER_RADIUS.lg} ${SHADOWS.custom} border ${BORDER_OPACITY.standard}`,
	
	// Standard button styling
	buttonPrimary: `${BUTTON_HEIGHTS.md} ${BORDER_RADIUS.md} ${SHADOWS.custom}`,
	
	// Standard container padding
	containerPadding: 'p-4 md:p-6 lg:p-8',
} as const;

/**
 * Type exports for TypeScript support
 */
export type InputHeight = typeof INPUT_HEIGHTS[keyof typeof INPUT_HEIGHTS];
export type ButtonHeight = typeof BUTTON_HEIGHTS[keyof typeof BUTTON_HEIGHTS];
export type BorderOpacity = typeof BORDER_OPACITY[keyof typeof BORDER_OPACITY];
export type BorderRadius = typeof BORDER_RADIUS[keyof typeof BORDER_RADIUS];
export type FontSize = typeof FONT_SIZES[keyof typeof FONT_SIZES];
export type Shadow = typeof SHADOWS[keyof typeof SHADOWS];

